import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import { SnackBarType } from '../../../shared/components/snack-bar/snack-bar.component';
import { NOTIFICATIONS } from '../../../shared/constants';
import { BadResponseWithError } from '../../../shared/models/bad-response-with-error.interface';
import { NotificationsService } from '../../../shared/services/notifications.service';
import { ProfileTeamResponse } from '../../../team/models/api/responses/team.response';
import { ProfileTeam } from '../../../team/models/team.interface';
import { ProfileTeamsService } from '../../services/profile-teams.service';
import { ProfileTeamsActions } from '../actions/actions-type.action';
import { PermissionsService } from '../../../permissions/services/permissions.service';
import { FinalizedPermissionsResponse } from '../../../permissions/models/responses/finalized-permissions.response';

@Injectable()
export class ProfileTeamsEffect {
    private actions$ = inject(Actions);
    private profileTeamsService = inject(ProfileTeamsService);
    private notificationsService = inject(NotificationsService);
    private modalService = inject(ModalWindowsService);
    private permissionsService = inject(PermissionsService);

    getAllTeams$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.profileGetAllTeams),
            switchMap((action) =>
                this.profileTeamsService
                    .getAllTeams(action.payload.profileId)
                    .pipe(
                        mergeMap((teams: ProfileTeam[]) => [
                            ProfileTeamsActions.profileGetAllTeamsSuccess({
                                payload: teams,
                            }),
                        ]),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileTeamsActions.profileGetAllTeamsFailure({
                                    payload: response,
                                })
                            )
                        )
                    )
            )
        )
    );

    getAllTeamsAsLeader$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.profileGetAllTeamsAsLeader),
            switchMap((action) =>
                this.profileTeamsService
                    .getAllTeamsAsLeader(action.payload.profileId)
                    .pipe(
                        mergeMap((teams: ProfileTeam[]) => [
                            ProfileTeamsActions.profileGetAllTeamsAsLeaderSuccess(
                                {
                                    payload: teams,
                                }
                            ),
                        ]),
                        catchError((response: BadResponseWithError) =>
                            of(
                                ProfileTeamsActions.profileGetAllTeamsAsLeaderFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            )
                        )
                    )
            )
        )
    );

    createTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.profileCreateTeam),
            exhaustMap((action) =>
                this.profileTeamsService.createTeam(action.payload).pipe(
                    map((team: ProfileTeamResponse) => {
                        this.modalService.closeCreateTeamModal();

                        this.notificationsService.openSnackBar({
                            title: NOTIFICATIONS.TEAM_CREATED,
                            type: SnackBarType.Success,
                        });

                        this.modalService.openTeamInvitePlayersModal(team);

                        return ProfileTeamsActions.profileCreateTeamSuccess({
                            payload: team,
                        });
                    }),
                    catchError((response: BadResponseWithError) =>
                        of(
                            ProfileTeamsActions.profileCreateTeamFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )
    );

    deleteTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.profileDeleteTeam),
            exhaustMap((action) =>
                this.profileTeamsService.deleteTeam(action.payload.teamId).pipe(
                    mergeMap(() => {
                        this.notificationsService.openSnackBar({
                            title: NOTIFICATIONS.TEAM_DELETED,
                            type: SnackBarType.Success,
                        });
                        return [
                            ProfileTeamsActions.profileDeleteTeamSuccess({
                                payload: {
                                    teamId: action.payload.teamId,
                                },
                            }),
                        ];
                    }),
                    catchError((response: BadResponseWithError) => {
                        this.notificationsService.openSnackBar({
                            title: 'Error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
                        });

                        return of(
                            ProfileTeamsActions.profileDeleteTeamFailure({
                                payload: response,
                            })
                        );
                    })
                )
            )
        )
    );

    banTeam$ = createEffect(()=>
    this.actions$.pipe(
        ofType(ProfileTeamsActions.banTeamEntity),
        exhaustMap((action)=>
        {
            return this.profileTeamsService.banTeam(action.teamId).pipe(
                mergeMap(()=>{
                    this.notificationsService.openSnackBar({
                        title: NOTIFICATIONS.TEAM_BANNED,
                        type: SnackBarType.Success,
                    });
                    window.location.reload();
                    return [
                        ProfileTeamsActions.banTeamSuccess(),
                    ];
                }),
                catchError((response: BadResponseWithError) => {
                    this.notificationsService.openSnackBar({
                            title: 'Error',
                            message: response.error.message,
                            type: SnackBarType.Error,
                            error: response.error,
            });

            return of(
                ProfileTeamsActions.banTeamFailure({
                    payload: response,
                })
            );
        })
            )
        })
    ))

    banUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.banUsers),
            exhaustMap((action) =>
                {
                    const sideEffects: any[] = [];
                    if(action.payload.currentTeamId){
                        sideEffects.push(ProfileTeamsActions.banTeamEntity({teamId:action.payload.currentTeamId}));
                    }
                    return this.profileTeamsService.banUsers(action.payload).pipe(
                        mergeMap(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TEAM_MEMBER_BANNED,
                                type: SnackBarType.Success,
                            });
                            this.modalService.closeBanUserModal();
                            if(!action.payload.currentTeamId){
                                window.location.reload();
                            }
                            return [
                                ProfileTeamsActions.banUsersSuccess(),
                                ...sideEffects
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                ProfileTeamsActions.banUsersFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
                }
            )
        )
    );

    fetchTeamsPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.fetchTeamsPermissionsRequest),
            mergeMap((action) => {


                return this.permissionsService
                    .fetchPermissionRequest(action.payload.userId, action.payload.permissionContexts)
                    .pipe(
                        map((response: FinalizedPermissionsResponse) =>
                            ProfileTeamsActions.fetchTeamsPermissionsRequestsSuccess(
                                {
                                    payload: {
                                        teamId: action.payload.permissionContexts[0].contextEntityId,
                                        response: response
                                    }
                                }
                            )
                        ),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: response.error.message,
                                type: SnackBarType.Error,
                            });

                            return of(
                                ProfileTeamsActions.fetchTeamsPermissionsRequestsFailure(
                                    {
                                        payload: response,
                                    }
                                )
                            );
                        })
                )
                }

            )
        )
    );
    timeoutUsers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.timeoutUsers),
            exhaustMap((action) =>
                {
                    const sideEffects: any[] = [];
                    if(action.payload.currentTeamId){
                        sideEffects.push(ProfileTeamsActions.timeoutTeamEntity({teamId:action.payload.currentTeamId}));
                    }
                    return this.profileTeamsService.timeoutUsers(action.payload).pipe(
                        mergeMap(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TEAM_MEMBER_TIMEOUTED,
                                type: SnackBarType.Success,
                            });
                            this.modalService.closeBanUserModal();
                            if(!action.payload.currentTeamId){
                                window.location.reload();
                            }
                            return [
                                ProfileTeamsActions.timeoutUsersSuccess(),
                                ...sideEffects
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                ProfileTeamsActions.timeoutUsersFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
                }
            )
        )
    );
    timeoutTeam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ProfileTeamsActions.timeoutTeamEntity),
            exhaustMap((action) =>
                {
                    return this.profileTeamsService.timeoutTeam(action.teamId, action?.timeOutUntil).pipe(
                        mergeMap(() => {
                            this.notificationsService.openSnackBar({
                                title: NOTIFICATIONS.TEAM_MEMBER_TIMEOUTED,
                                type: SnackBarType.Success,
                            });
                            this.modalService.closeBanUserModal();
                            window.location.reload();
                            return [
                                ProfileTeamsActions.timeoutTeamSuccess(),
                            ];
                        }),
                        catchError((response: BadResponseWithError) => {
                            this.notificationsService.openSnackBar({
                                title: 'Error',
                                message: response.error.message,
                                type: SnackBarType.Error,
                                error: response.error,
                            });

                            return of(
                                ProfileTeamsActions.timeoutTeamFailure({
                                    payload: response,
                                })
                            );
                        })
                    )
                }
            )
        )
    );
}
